import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { orderAPI } from '../../services/api';

// Async Thunks
export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await orderAPI.createOrder(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create order');
    }
  }
);

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getOrders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderAPI.getOrderById(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order details');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateOrderStatus',
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      const response = await orderAPI.updateOrderStatus(orderId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update order status');
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await orderAPI.cancelOrder(orderId, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
    }
  }
);

export const requestReturn = createAsyncThunk(
  'orders/requestReturn',
  async ({ orderId, returnData }, { rejectWithValue }) => {
    try {
      const response = await orderAPI.requestReturn(orderId, returnData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to request return');
    }
  }
);

export const trackOrder = createAsyncThunk(
  'orders/trackOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderAPI.trackOrder(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to track order');
    }
  }
);

export const addOrderNote = createAsyncThunk(
  'orders/addOrderNote',
  async ({ orderId, note }, { rejectWithValue }) => {
    try {
      const response = await orderAPI.addOrderNote(orderId, note);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add order note');
    }
  }
);

// Initial State
const initialState = {
  orders: [],
  currentOrder: null,
  orderTracking: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  },
  filters: {
    status: 'all',
    dateRange: null,
    searchTerm: '',
  },
  loading: false,
  createLoading: false,
  trackingLoading: false,
  error: null,
  successMessage: null,
};

// Order Slice
const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    clearOrderTracking: (state) => {
      state.orderTracking = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        dateRange: null,
        searchTerm: '',
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateOrderInList: (state, action) => {
      const { orderId, updates } = action.payload;
      const orderIndex = state.orders.findIndex(order => order._id === orderId);
      if (orderIndex !== -1) {
        state.orders[orderIndex] = { ...state.orders[orderIndex], ...updates };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createLoading = false;
        state.currentOrder = action.payload;
        state.successMessage = 'Order created successfully!';
        state.error = null;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
        state.error = null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Order By ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Order Status
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const updatedOrder = action.payload;
        const orderIndex = state.orders.findIndex(order => order._id === updatedOrder._id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = updatedOrder;
        }
        if (state.currentOrder && state.currentOrder._id === updatedOrder._id) {
          state.currentOrder = updatedOrder;
        }
        state.successMessage = 'Order status updated successfully!';
      })
      
      // Cancel Order
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const cancelledOrder = action.payload;
        const orderIndex = state.orders.findIndex(order => order._id === cancelledOrder._id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = cancelledOrder;
        }
        if (state.currentOrder && state.currentOrder._id === cancelledOrder._id) {
          state.currentOrder = cancelledOrder;
        }
        state.successMessage = 'Order cancelled successfully!';
      })
      
      // Request Return
      .addCase(requestReturn.fulfilled, (state, action) => {
        const returnRequest = action.payload;
        const orderIndex = state.orders.findIndex(order => order._id === returnRequest.orderId);
        if (orderIndex !== -1) {
          state.orders[orderIndex].returnRequest = returnRequest;
        }
        if (state.currentOrder && state.currentOrder._id === returnRequest.orderId) {
          state.currentOrder.returnRequest = returnRequest;
        }
        state.successMessage = 'Return request submitted successfully!';
      })
      
      // Track Order
      .addCase(trackOrder.pending, (state) => {
        state.trackingLoading = true;
        state.error = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.orderTracking = action.payload;
        state.error = null;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.trackingLoading = false;
        state.error = action.payload;
      })
      
      // Add Order Note
      .addCase(addOrderNote.fulfilled, (state, action) => {
        const { orderId, note } = action.payload;
        const orderIndex = state.orders.findIndex(order => order._id === orderId);
        if (orderIndex !== -1) {
          if (!state.orders[orderIndex].notes) {
            state.orders[orderIndex].notes = [];
          }
          state.orders[orderIndex].notes.push(note);
        }
        if (state.currentOrder && state.currentOrder._id === orderId) {
          if (!state.currentOrder.notes) {
            state.currentOrder.notes = [];
          }
          state.currentOrder.notes.push(note);
        }
        state.successMessage = 'Note added successfully!';
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  clearCurrentOrder,
  clearOrderTracking,
  setFilters,
  clearFilters,
  setPagination,
  updateOrderInList,
} = orderSlice.actions;

export default orderSlice.reducer;